<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="resolvedPlaceholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxDisplayLength"
    :status="status"
    class="ant-masked-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template v-if="resolvedPrefixIcon" #prefix>
      <i :class="resolvedPrefixIcon" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

export type MaskPreset = "custom" | "cmnd" | "cccd" | "phone" | "tax" | "bank";

interface Props {
  modelValue?: string;
  /** Preset sẵn: cmnd | cccd | phone | tax | bank | custom */
  preset?: MaskPreset;
  /**
   * Pattern mask:
   * - # = chữ số
   * - A = chữ cái
   * - * = chữ hoặc số
   * - ký tự khác giữ nguyên (vd: khoảng trắng, gạch)
   * Ví dụ: "#### #### ####" hoặc "###-###-####"
   */
  mask?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  /** true = emit raw (không có ký tự mask), false = emit chuỗi đã mask */
  unmasked?: boolean;
  prefixIcon?: string;
}

const PRESETS: Record<Exclude<MaskPreset, "custom">, { mask: string; placeholder: string; icon: string }> = {
  cmnd: {
    mask: "#########",
    placeholder: "Nhập CMND (9 số)",
    icon: "fas fa-id-card",
  },
  cccd: {
    mask: "############",
    placeholder: "Nhập CCCD (12 số)",
    icon: "fas fa-id-card",
  },
  phone: {
    mask: "#### ### ###",
    placeholder: "0xxx xxx xxx",
    icon: "fas fa-phone",
  },
  tax: {
    mask: "##########-###",
    placeholder: "Mã số thuế",
    icon: "fas fa-file-invoice",
  },
  bank: {
    mask: "#### #### #### ####",
    placeholder: "Số tài khoản / thẻ",
    icon: "fas fa-credit-card",
  },
};

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  preset: "custom",
  mask: "",
  placeholder: "",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  unmasked: true,
  prefixIcon: "",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "change", value: string, masked: string): void;
}>();

const displayValue = ref("");

const activeMask = computed(() => {
  if (props.preset !== "custom" && PRESETS[props.preset]) {
    return PRESETS[props.preset].mask;
  }
  return props.mask || "";
});

const resolvedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder;
  if (props.preset !== "custom" && PRESETS[props.preset]) {
    return PRESETS[props.preset].placeholder;
  }
  return "";
});

const resolvedPrefixIcon = computed(() => {
  if (props.prefixIcon) return props.prefixIcon;
  if (props.preset !== "custom" && PRESETS[props.preset]) {
    return PRESETS[props.preset].icon;
  }
  return "";
});

const maxDisplayLength = computed(() => activeMask.value.length || undefined);

const isMaskToken = (char: string) => char === "#" || char === "A" || char === "*";

const matchToken = (token: string, char: string) => {
  if (token === "#") return /\d/.test(char);
  if (token === "A") return /[a-zA-Z]/.test(char);
  if (token === "*") return /[a-zA-Z0-9]/.test(char);
  return false;
};

/** Lấy chuỗi raw theo thứ tự token trong mask */
const extractRaw = (value: string): string => {
  const mask = activeMask.value;
  if (!mask) return value;

  const tokens = mask.split("").filter(isMaskToken);
  let raw = "";

  for (const ch of value) {
    if (raw.length >= tokens.length) break;
    if (matchToken(tokens[raw.length], ch)) {
      raw += ch;
    }
  }

  return raw;
};

const applyMask = (raw: string): { masked: string; unmasked: string } => {
  const mask = activeMask.value;
  if (!mask) {
    return { masked: raw, unmasked: raw };
  }

  let masked = "";
  let unmasked = "";
  let rawIndex = 0;

  for (let i = 0; i < mask.length; i++) {
    const token = mask[i];

    if (!isMaskToken(token)) {
      if (rawIndex < raw.length) {
        masked += token;
      }
      continue;
    }

    if (rawIndex >= raw.length) break;

    const ch = raw[rawIndex];
    if (!matchToken(token, ch)) break;

    masked += ch;
    unmasked += ch;
    rawIndex++;
  }

  return { masked, unmasked };
};

watch(
  () => [props.modelValue, activeMask.value] as const,
  ([val]) => {
    const source = String(val ?? "");
    const raw = props.unmasked ? extractRaw(source) : extractRaw(source);
    displayValue.value = applyMask(raw).masked;
  },
  { immediate: true }
);

const onInput = (val: string) => {
  const raw = extractRaw(val);
  const { masked, unmasked } = applyMask(raw);
  displayValue.value = masked;
  const emitValue = props.unmasked ? unmasked : masked;
  emit("update:modelValue", emitValue);
  emit("change", emitValue, masked);
};

const onBlur = (e: FocusEvent) => emit("blur", e);
const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Masked Input
  Global: <h-masked v-model="cccd" preset="cccd" />
           <h-masked v-model="custom" mask="####-##-##" />
  Import: import { MaskedInput } from '@/components/ant/input'

  Props: preset (cmnd|cccd|phone|tax|bank|custom), mask, unmasked
  Mask tokens: # = số, A = chữ, * = chữ/số
  Events: update:modelValue, change(value, masked), blur, pressEnter
  Docs: src/components/ant/input/README.md
-->

<!--
  Hướng dẫn sử dụng — Masked Input
  Global: <h-masked v-model="cccd" preset="cccd" />
           <h-masked v-model="custom" mask="####-##-##" />
  Import: import { MaskedInput } from '@/components/ant/input'

  Props: preset (cmnd|cccd|phone|tax|bank|custom), mask, unmasked
  Mask tokens: # = số, A = chữ, * = chữ/số
  Events: update:modelValue, change(value, masked), blur, pressEnter
  Docs: src/components/ant/input/README.md
-->
